import { AudioOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Input, message, Modal, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import googleAIService from '../../../services/googleAIService';
import './VoiceInputField.css';

// Маппинг языков для Web Speech API
const SPEECH_LANG_MAP = {
  'ru': 'ru-RU',
  'ky': 'ky-KG',
  'en': 'en-US'
};

const VoiceInputField = ({ 
  value, 
  onChange, 
  placeholder, 
  disabled = false,
  maxLength = 1000 
}) => {
  const { i18n, t } = useTranslation('global');
  const [isRecording, setIsRecording] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const recordingIntervalRef = useRef(null);
  const recognitionRef = useRef(null);

  const startRecording = async () => {
    try {
      // Проверяем поддержку Web Speech API
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        message.error('Ваш браузер не поддерживает голосовой ввод. Используйте Chrome, Edge или Safari.');
        return;
      }

      const recognition = new SpeechRecognition();
      
      // Получаем язык распознавания речи на основе текущего языка интерфейса
      const currentLang = i18n.language || 'ru';
      const speechLang = SPEECH_LANG_MAP[currentLang] || 'ru-RU';
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = speechLang; // Используем текущий язык
      recognition.maxAlternatives = 1;
      

      let finalTranscript = '';

      recognition.onresult = (event) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        if (event.error === 'no-speech') {
          message.warning('Речь не обнаружена. Говорите громче.');
        } else if (event.error === 'audio-capture') {
          message.error('Не удалось получить доступ к микрофону.');
        } else if (event.error === 'not-allowed') {
          message.error('Доступ к микрофону запрещен. Разрешите доступ в настройках браузера.');
        } else {
          message.error(`Ошибка распознавания: ${event.error}`);
        }
        
        stopRecording();
      };

      recognition.onend = () => {
        if (finalTranscript.trim()) {
          processTranscript(finalTranscript.trim());
        } else {
          setIsRecording(false);
          setIsModalVisible(false);
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
      
      setIsRecording(true);
      setIsModalVisible(true);
      setRecordingTime(0);
      
      // Таймер записи
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      message.error('Не удалось начать запись. Проверьте доступ к микрофону.');
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    setIsRecording(false);
    
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };

  const processTranscript = async (transcript) => {
    try {
      setIsProcessing(true);
      
      // Исправляем грамматические ошибки через Google AI
      let correctedTranscript = transcript;
      
      try {
        correctedTranscript = await googleAIService.fixGrammarErrors(transcript, 'ru');
      } catch (error) {
        console.warn('Не удалось исправить грамматику, используем оригинальный текст:', error);
        // Используем оригинальный текст, если исправление не удалось
        correctedTranscript = transcript;
      }
      
      // Заменяем весь текст новым голосовым вводом
      // Обрезаем до maxLength
      const newValue = correctedTranscript.trim().slice(0, maxLength);
      
      onChange(newValue);
      setIsModalVisible(false);
      setIsProcessing(false);
      message.success(t('voice.voiceInputReplaced'));
      
    } catch (error) {
      console.error('Error processing transcript:', error);
      message.error(t('voice.textProcessingError'));
      setIsModalVisible(false);
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCancel = () => {
    if (isRecording) {
      stopRecording();
    }
    setIsModalVisible(false);
  };

  // Cleanup при размонтировании
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="voice-input-field">
      <div className="input-with-voice">
        <Input.TextArea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          showCount
          autoSize={{ minRows: 3, maxRows: 8 }}
          className="voice-textarea"
        />
        
        <Tooltip title={t('voice.realtimeInput')}>
          <Button
            type="text"
            icon={<AudioOutlined />}
            onClick={startRecording}
            disabled={disabled}
            className="voice-button"
            size="small"
          />
        </Tooltip>
      </div>

      <Modal
        title={t('voice.realtimeInput')}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        centered
        className="voice-modal"
      >
        <div className="voice-recording">
          {isRecording ? (
            <div className="recording-active">
              <div className="recording-indicator">
                <div className="pulse-dot" />
                <span>{t('voice.recording')} {formatTime(recordingTime)}</span>
              </div>
              <Button
                type="primary"
                danger
                icon={<StopOutlined />}
                onClick={stopRecording}
                size="large"
              >
                {t('voice.stopRecording')}
              </Button>
              <p className="recording-tip">
                {t('voice.tip1')}
              </p>
            </div>
          ) : isProcessing ? (
            <div className="processing">
              <div className="loading-spinner" />
              <p>{t('voice.fixingErrors')}</p>
            </div>
          ) : (
            <div className="processing">
              <div className="loading-spinner" />
              <p>{t('voice.processingAudio')}</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VoiceInputField;